import { Controller } from "@hotwired/stimulus";

// 購入額ヒント（投資目安額UI）: フロートの表示/バツ消し・モーダル開閉・概算プレビュー・bfcache復元。
// 入力フォームと結果の描画は Turbo Frame/Stream（サーバ）が担うため、JS は画面状態を持たない。
// bfcache（Turbo Drive OFF でも効く）で戻ると凍結DOMが復元されるので、フロート（吹き出し＋マスコット）が
// 変わるたびに sessionStorage へ保存し（MutationObserver）、bfcache 復帰時だけ貼り直す。
//   - 保存単位はフロート本体(floatBody=吹き出し＋マスコット)。文言だけだと戻ったとき表情が古いまま食い違う。
//   - キーはユーザー単位。別ユーザーでログインし直したとき前ユーザーの内容が残らないようにする。
//   - 復元は pageshow の persisted（bfcache）時のみ。通常読込はサーバ描画が正しいので触らない。
// turbo:submit-end は Drive OFF + frame 送信では発火/タイミングが不安定だったため、DOM 変化を直接監視する。
// 効果測定の投げっぱなし POST と sessionStorage はこのファイルにインラインで閉じる。
const FLOAT_BODY_KEY_PREFIX = "investment_hint_float_body_";

export class InvestmentHintController extends Controller {
  static targets = ["float", "floatBody", "floatMascot", "modal", "incomeInput", "traitRadio", "expensePreview", "traitModeRadio", "amountModeRadio", "traitTab"];
  static values = {
    displayedUrl: String,
    openedUrl: String,
    formUrl: String,
    expenseRatios: Object,
    alreadyDisplayed: Boolean,
    customerKey: String,
  };

  #floatBodyObserver = null;
  #mousedownOnBackdrop = false;

  connect() {
    // 口座登録完了画面にはフロートが無く、ボタンからモーダルを開く
    if (!this.hasFloatTarget) return;

    // フロート（吹き出し＋マスコット）を bfcache/通常キャッシュ復元に強くする。
    //   - 保存済みが現在のサーバ描画より新しければ貼り直す（戻る操作で計算前スナップショットが
    //     キャッシュ復元されても、別画面での最新算出に揃う）。
    //   - そうでなければ現在のサーバ描画を保存し直して基準にする。
    //   - 以降フロートが Turbo Stream 等で書き換わるたびに保存する（MutationObserver）。
    // 新旧の判定は「算出時刻（calculated_at）＝版」で行う。DOM側の版はマスコットの data-float-version、
    // 保存側は storage に一緒に入れる。has-amount のような状態フラグは古いキャッシュにも付くため使えない。
    // キーはユーザー単位なので、別ユーザーの内容を復元してしまう心配はない。
    if (this.hasFloatBodyTarget) {
      // 保存済みが現在のサーバ描画より新しければ貼り直し、そうでなければ現状を保存して基準にする
      if (!this.#restoreFloatIfFresher()) this.#saveFloat();
      this.#floatBodyObserver = new MutationObserver(() => this.#saveFloat());
      this.#floatBodyObserver.observe(this.floatBodyTarget, { childList: true, subtree: true });
    }

    // 記録済みなら送らない（サーバは初回しか書き込まない）
    if (!this.alreadyDisplayedValue) this.#post(this.displayedUrlValue);
  }

  disconnect() {
    this.#floatBodyObserver?.disconnect();
  }

  // ==== フロート ====

  // ✕ で当画面のみ非表示にする。永続化しないので、リロードすると再表示される（誤操作で二度と出せない事故を防ぐ）
  dismiss() {
    this.floatTarget.hidden = true;
  }

  // bfcache 復帰時に、保存済みが復元DOMより新しければ貼り直す（pageshow から呼ぶ）。
  // bfcache は connect() が再実行されないため、connect() とは別にここでも復元する必要がある。
  restoreFloatOnBfcache(event) {
    if (!event.persisted || !this.hasFloatBodyTarget) return;

    this.#restoreFloatIfFresher();
  }

  // 保存済みが現在のサーバ描画（DOM）より新しければ貼り直す。貼り直したら true を返す。
  // 新旧は版（マスコットの data-float-version＝算出時刻）で判定する。
  #restoreFloatIfFresher() {
    const stored = this.#readStoredFloat();
    if (stored && stored.v > this.#floatVersion()) {
      this.floatBodyTarget.innerHTML = stored.html;
      return true;
    }
    return false;
  }

  // 現在のフロート（吹き出し＋マスコット）を版付きで保存する。
  #saveFloat() {
    this.#writeStorage(this.#floatStorageKey(), JSON.stringify({ v: this.#floatVersion(), html: this.floatBodyTarget.innerHTML }));
  }

  // 保存済みフロート { v, html } を返す（未保存・壊れている・旧形式なら null）。
  #readStoredFloat() {
    const raw = this.#readStorage(this.#floatStorageKey());
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return typeof parsed?.html === "string" ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  // DOM 上のフロートの版（マスコットの data-float-version＝算出時刻。未算出/未接続は 0）
  #floatVersion() {
    return (this.hasFloatMascotTarget && Number(this.floatMascotTarget.dataset.floatVersion)) || 0;
  }

  // sessionStorage のキー（ユーザー単位。別ユーザーの内容を復元しないため）
  #floatStorageKey() {
    return `${FLOAT_BODY_KEY_PREFIX}${this.customerKeyValue}`;
  }

  // ==== モーダル ====

  openForm() {
    this.#reloadModalForm(); // キャッシュ復元で凍結された古い中身を、最新レコードのフォームに取り直す
    this.#updateExpensePreview();
    this.modalTarget.hidden = false; // FOUC 防止の hidden を外す（以降は is-open のフェードで開閉）
    void this.modalTarget.offsetHeight; // hidden 解除直後に is-open を付けるとフェードが飛ぶため一度リフローを挟む
    this.modalTarget.classList.add("is-open");
    this.element.ownerDocument.body.style.overflow = "hidden"; // 背後のスクロールを止める
    this.#setChatWidgetVisible(false); // 前面に被る AIチャット(Tayori)を退避
    this.#post(this.openedUrlValue);
  }

  // モーダルを開くたびにフォームフレームをサーバから取り直し、常に最新レコードのプリフィルにする。
  // 算出後は結果フレーム（src 無し）に置き換わるため、src が無ければ張り直し、あれば reload で再取得する。
  #reloadModalForm() {
    const frame = document.getElementById("investment_hint_body");
    if (!frame || !this.formUrlValue) return;

    if (frame.src) {
      frame.reload();
    } else {
      frame.src = this.formUrlValue;
    }
  }

  close() {
    this.#closeModal();
  }

  // 暗幕上で mousedown したかを記録する。
  // 入力欄内で選択を始めて暗幕でボタンを離すと click.target が暗幕になり誤クローズするため、その判定に使う。
  backdropMousedown(event) {
    this.#mousedownOnBackdrop = event.target === this.modalTarget;
  }

  clickOutside(event) {
    // mousedown と click(mouseup) の両方が暗幕上のときだけ閉じる（テキスト選択での誤クローズを防ぐ）
    if (event.target === this.modalTarget && this.#mousedownOnBackdrop) this.#closeModal();
    this.#mousedownOnBackdrop = false;
  }

  #closeModal() {
    this.modalTarget.classList.remove("is-open");
    this.element.ownerDocument.body.style.overflow = "";
    this.#setChatWidgetVisible(true);
  }

  // ==== 概算プレビュー ====

  // 「計算に戻る」等でフォームが再描画されたら、プリフィル値から概算を計算し直す
  expensePreviewTargetConnected() {
    this.#updateExpensePreview();
  }

  // 再訪でフォームが再描画されたとき（プリフィル）も、収入0なら金額入力モードへ制限したい。
  // #applyIncomeMode は「収入入力」と「モードのラジオ」両方の target が必要。
  // フォームでは収入入力がモードのラジオより上にあり、DOM 順で先に接続されるため、
  // trait ラジオが接続するこの時点では収入入力は既に接続済み（＝両方揃っている）。ここで適用する。
  traitModeRadioTargetConnected() {
    this.#applyIncomeMode();
    this.#syncIncomeRequired();
  }

  // モード切替時（タイプで選ぶ/金額で入力）にも概算を出し直す（trait へ戻したとき 0 のままにならないように）
  // 収入の required もモードに追従させる（金額入力モードでは収入を算出に使わないため必須にしない）。
  modeChanged() {
    this.#updateExpensePreview();
    this.#syncIncomeRequired();
  }

  traitChanged() {
    this.#updateExpensePreview();
  }

  // 収入入力中（フォーカス中）は概算だけ更新し、モードの強制切替はしない（0を経由しても勝手に切り替えない）
  incomeChanged() {
    this.#updateExpensePreview();
  }

  // 収入入力にフォーカスが当たっている間は制限を解除する（編集中は自由にタイプ選択できるようにする）
  incomeFocused() {
    if (!this.hasTraitModeRadioTarget) return;

    this.#setTraitModeAvailable(true);
  }

  // フォーカスが外れたとき（＝入力確定）に、収入0なら金額入力モードへ強制切替する
  incomeBlurred() {
    this.#applyIncomeMode();
  }

  // 支出（概算・万円）＝ 手取り月収（万円）× 選択中タイプの係数。0 のときだけ「0円」と表示する
  #updateExpensePreview() {
    if (!this.hasExpensePreviewTarget || !this.hasIncomeInputTarget) return;

    const ratio = Number(this.expenseRatiosValue[this.#selectedTrait()]) || 0;
    const expenseMan = Math.round(this.#incomeMan() * ratio);
    this.expensePreviewTarget.textContent = expenseMan === 0 ? "0円" : `${expenseMan.toLocaleString("ja-JP")}万円`;
  }

  // 収入が「明示的に0」のとき、支出「タイプで選ぶ」を無効化して金額入力モードに固定する。
  // 支出＝収入×係数のため収入0では常に0になり、タイプ選択が意味を持たない（支出0で計算されてしまう）ため。
  // 空欄(未入力)は制限しない：初回プリフィルは収入が空欄なので、金額入力しか選べないと誤解させないため
  // （収入欄は required で、空欄のままでは算出できない）。
  #applyIncomeMode() {
    if (!this.hasIncomeInputTarget || !this.hasTraitModeRadioTarget || !this.hasAmountModeRadioTarget) return;

    const restrict = this.#incomeIsZero();
    this.#setTraitModeAvailable(!restrict);
    // 収入0なのに「タイプで選ぶ」のままなら、金額入力モードへ切り替える
    if (restrict && this.traitModeRadioTarget.checked) this.#switchToAmountMode();
  }

  // 収入入力欄の生値（数字と小数点のみ。カンマ等を除去）
  #incomeRaw() {
    return String(this.incomeInputTarget.value).replace(/[^0-9.]/g, "");
  }

  // 収入入力欄の値（万円・数値）。空/不正は0にする
  #incomeMan() {
    return Number(this.#incomeRaw()) || 0;
  }

  // 収入が「明示的に0」か。空欄(未入力)は false（初回プリフィル等で金額入力に固定されるのを防ぐ）
  #incomeIsZero() {
    const raw = this.#incomeRaw();
    return raw !== "" && Number(raw) === 0;
  }

  // 収入の required をモードに追従させる。
  // タイプ選択モードは収入から支出を概算するため必須、金額入力モードは収入を算出に使わないため任意にする。
  #syncIncomeRequired() {
    if (!this.hasIncomeInputTarget || !this.hasTraitModeRadioTarget) return;

    this.incomeInputTarget.required = this.traitModeRadioTarget.checked;
  }

  // 支出「タイプで選ぶ」を選択可能/不可にする（不可のときタブをグレーアウト）
  #setTraitModeAvailable(available) {
    this.traitModeRadioTarget.disabled = !available;
    if (this.hasTraitTabTarget) this.traitTabTarget.classList.toggle("is-disabled", !available);
  }

  // 金額入力モードへ切り替える（change を発火し、CSS の表示切替と概算プレビューを更新させる）
  #switchToAmountMode() {
    this.amountModeRadioTarget.checked = true;
    this.amountModeRadioTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // 選択中の支出タイプ（"frugal" / "standard" / "spender"）
  #selectedTrait() {
    return this.traitRadioTargets.find((radio) => radio.checked)?.value ?? "standard";
  }

  // ==== 低レベルヘルパ（通信・保存・ウィジェット）====

  // 効果測定の記録用。結果を待たず、失敗しても UI は継続させる
  #post(url) {
    if (!url) return;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
      },
    }).catch(() => {});
  }

  // sessionStorage。プライベートブラウズ等で使えなくても UI を止めない
  #readStorage(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  #writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {
      // 利用不可でも UI は成立させる
    }
  }

  // AIチャットウィジェット（Tayori）の表示/非表示
  #setChatWidgetVisible(visible) {
    const widget = document.getElementById("tayori-ai-chatbot-app");
    if (widget) widget.style.display = visible ? "" : "none";
  }
}
